import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Clock, Calendar, Trash2, Edit, Star, Filter } from 'lucide-react';
import { Task } from '../types/task';
import { fetchTasks, completeTask, deleteTask } from '../services/tasks';
import { format } from 'date-fns';
import { useNotification } from '../contexts/NotificationContext';
import { formatDateForDisplay } from '../utils/dateUtils';

const FilterContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterButton = styled(motion.button)<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 2px solid ${(props: { active: boolean }) => props.active ? '#F57C00' : '#e2e8f0'};
  background: ${(props: { active: boolean }) => props.active ? '#fef3c7' : 'white'};
  color: ${(props: { active: boolean }) => props.active ? '#E65100' : '#64748b'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const TaskListContainer = styled.div`
  padding: 0 8px;
`;

const TaskCard = styled(motion.div)<{ priority: string; completed: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border-left: 4px solid ${(props: { priority: string }) => {
    switch (props.priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  }};
  transition: all 0.2s ease;
  opacity: ${(props: { completed: boolean }) => props.completed ? 0.8 : 1};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
`;

const TaskTitle = styled.h3<{ completed: boolean }>`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${(props: { completed: boolean }) => props.completed ? '#64748b' : '#1e293b'};
  flex: 1;
  text-decoration: ${(props: { completed: boolean }) => props.completed ? 'line-through' : 'none'};
`;

const TaskActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled(motion.button)<{ variant: 'complete' | 'edit' | 'delete' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props: { variant: 'complete' | 'edit' | 'delete' }) => {
    switch (props.variant) {
      case 'complete':
        return '#10b981';
      case 'edit':
        return '#3b82f6';
      case 'delete':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }};
  color: white;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const TaskDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`;

const TaskDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const TaskDescription = styled.p<{ completed: boolean }>`
  color: ${(props: { completed: boolean }) => props.completed ? '#94a3b8' : '#475569'};
  margin: 0 0 16px 0;
  line-height: 1.6;
  font-size: 14px;
`;

const PriorityBadge = styled.span<{ priority: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: ${(props: { priority: string }) => {
    switch (props.priority) {
      case 'high': return '#fef2f2';
      case 'medium': return '#fffbeb';
      case 'low': return '#f0fdf4';
      default: return '#f1f5f9';
    }
  }};
  color: ${(props: { priority: string }) => {
    switch (props.priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      default: return '#64748b';
    }
  }};
  border: 1px solid ${(props: { priority: string }) => {
    switch (props.priority) {
      case 'high': return '#fecaca';
      case 'medium': return '#fed7aa';
      case 'low': return '#bbf7d0';
      default: return '#e2e8f0';
    }
  }};
`;

const CreditsBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #8B4513;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusBadge = styled.span<{ status?: 'pending_approval' | 'approved' | 'rejected' | 'pending_verification' | string }>`
  display: inline-block;
  min-width: 90px;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ status }: { status?: 'pending_approval' | 'approved' | 'rejected' | 'pending_verification' | string }) =>
    status === 'pending_approval'
      ? 'rgba(255, 225, 53, 0.18)'
      : status === 'approved'
      ? '#e0f7fa'
      : status === 'pending_verification'
      ? 'rgba(255, 225, 53, 0.18)'
      : '#f1f5f9'};
  color: ${({ status }: { status?: 'pending_approval' | 'approved' | 'rejected' | 'pending_verification' | string }) =>
    status === 'pending_approval'
      ? '#b45309'
      : status === 'approved'
      ? '#059669'
      : status === 'pending_verification'
      ? '#b45309'
      : '#64748b'};
  border: ${({ status }: { status?: 'pending_approval' | 'approved' | 'rejected' | 'pending_verification' | string }) =>
    status === 'pending_approval'
      ? '1.5px solid #FFE135'
      : status === 'pending_verification'
      ? '1.5px solid #FFE135'
      : '1.5px solid #e0e7ef'};
  margin-left: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  vertical-align: middle;
`;

interface TaskListProps {
  onTaskUpdate: (taskId: string) => void;
}

type StatusFilter = 'all' | 'approved' | 'pending_approval' | 'pending_verification' | 'completed' | 'verified' | 'rejected';

const TaskList: React.FC<TaskListProps> = ({ onTaskUpdate }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('approved');
  const { showError, showSuccess } = useNotification();

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      console.log('TaskList: Loading tasks...');
      const fetchedTasks = await fetchTasks();
      console.log('TaskList: Fetched tasks:', fetchedTasks);
      setTasks(fetchedTasks);
    } catch (error) {
      console.error('TaskList: Error loading tasks:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load tasks';
      showError(errorMessage);
      setTasks([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleCompleteTask = async (taskId: string) => {
    try {
      console.log('Completing task:', taskId);
      await completeTask(taskId);
      console.log('Task completion successful');
      
      // Reload tasks to get updated status
      await loadTasks();
      
      showSuccess('Task completed successfully!');
      onTaskUpdate(taskId);
    } catch (error) {
      console.error('Error completing task:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete task';
      showError(errorMessage);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      showSuccess('Task deleted successfully!');
      onTaskUpdate(taskId);
    } catch (error) {
      console.error('Error deleting task:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete task';
      showError(errorMessage);
    }
  };

  const pendingTasks = tasks.filter(task => task.status !== 'completed' && task.status !== 'verified');
  const completedTasks = tasks.filter(task => task.status === 'completed' || task.status === 'verified');

  // Helper function to check if task is completed
  const isTaskCompleted = (task: Task) => task.status === 'completed' || task.status === 'verified';

  // Filter tasks based on status filter
  const getFilteredTasks = () => {
    if (statusFilter === 'all') {
      return pendingTasks;
    } else if (statusFilter === 'completed') {
      return completedTasks;
    }
    return pendingTasks.filter(task => task.status === statusFilter);
  };

  const filteredTasks = getFilteredTasks();

  if (loading) {
    return <div>Loading tasks...</div>;
  }

  return (
    <TaskListContainer>
      {/* Status Filters */}
      <FilterContainer>
        <FilterButton
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Filter size={14} />
          All ({pendingTasks.length})
        </FilterButton>
        <FilterButton
          active={statusFilter === 'approved'}
          onClick={() => setStatusFilter('approved')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Check size={14} />
          Outstanding ({pendingTasks.filter(t => t.status === 'approved').length})
        </FilterButton>
        <FilterButton
          active={statusFilter === 'pending_approval'}
          onClick={() => setStatusFilter('pending_approval')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Clock size={14} />
          Pending Approval ({pendingTasks.filter(t => t.status === 'pending_approval').length})
        </FilterButton>
        <FilterButton
          active={statusFilter === 'pending_verification'}
          onClick={() => setStatusFilter('pending_verification')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Star size={14} />
          Pending Verification ({pendingTasks.filter(t => t.status === 'pending_verification').length})
        </FilterButton>
        <FilterButton
          active={statusFilter === 'completed'}
          onClick={() => setStatusFilter('completed')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Check size={14} />
          Completed ({completedTasks.length})
        </FilterButton>
      </FilterContainer>
      
      {filteredTasks.length === 0 && statusFilter === 'all' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No tasks yet. Create your first mission to get started!
          </div>
        </motion.div>
      )}

      {filteredTasks.length === 0 && statusFilter !== 'all' && statusFilter !== 'completed' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No {statusFilter.replace('_', ' ')} missions found.
          </div>
        </motion.div>
      )}

      {filteredTasks.length === 0 && statusFilter === 'completed' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No completed missions yet. Complete your first mission to see it here!
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {filteredTasks.map((task) => (
          <TaskCard
            key={task.id}
            priority={task.priority}
            completed={isTaskCompleted(task)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <TaskHeader>
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <TaskTitle completed={isTaskCompleted(task)}>{task.title}</TaskTitle>
                {task.status === 'pending_approval' && (
                  <StatusBadge status={task.status}>
                    Pending Approval
                  </StatusBadge>
                )}
                {task.status === 'pending_verification' && (
                  <StatusBadge status="pending_verification">
                    Pending Verification
                  </StatusBadge>
                )}
              </div>
              <TaskActions>
                {task.status !== 'pending_approval' && task.status !== 'pending_verification' && !isTaskCompleted(task) && (
                  <ActionButton
                    variant="complete"
                    onClick={() => handleCompleteTask(task.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Check />
                    Complete
                  </ActionButton>
                )}
                {task.status !== 'pending_verification' && !isTaskCompleted(task) && (
                  <ActionButton
                    variant="edit"
                    onClick={() => onTaskUpdate(task.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Edit />
                    Edit
                  </ActionButton>
                )}
                {task.status !== 'pending_verification' && !isTaskCompleted(task) && (
                  <ActionButton
                    variant="delete"
                    onClick={() => handleDeleteTask(task.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Trash2 />
                    Delete
                  </ActionButton>
                )}
              </TaskActions>
            </TaskHeader>

            {task.description && (
              <TaskDescription completed={isTaskCompleted(task)}>
                {task.description}
              </TaskDescription>
            )}

            <TaskDetails>
              <TaskDetail>
                <Calendar />
                Due: {formatDateForDisplay(task.dueDate)}
              </TaskDetail>
              <TaskDetail>
                <Clock />
                {task.estimatedDuration} min
              </TaskDetail>
              <PriorityBadge priority={task.priority}>
                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
              </PriorityBadge>
              <CreditsBadge>
                <Star size={12} />
                {task.credits} Credits
              </CreditsBadge>
            </TaskDetails>
          </TaskCard>
        ))}
      </AnimatePresence>
    </TaskListContainer>
  );
};

export default TaskList; 